import { iHolidayQuoteX } from "@/@sdk/holiday-quotes-x/holiday-quotes-x-model";
import { iContact } from "@/@sdk/crm/crm.models";
import { iOrganization, iUser } from "@/@sdk/user-management/user-management.models";
import { iBrand } from "@/@sdk/brands/brand-model";
import { buildPreviewGreetingText } from "./quote-theme-preview-data/preview-greeting";
import { buildPreviewItineraryBlocks } from "./quote-theme-preview-data/preview-itinerary-blocks";
import { buildPreviewWhatsIncludedBlock } from "./quote-theme-preview-data/preview-whats-included-block";
import {
  buildPreviewAgentDataBlock,
  buildPreviewCustomTemplateBlock,
  buildPreviewTrustPilotBlock,
} from "./quote-theme-preview-data/preview-brand-blocks";

export const QUOTE_THEME_PREVIEW_QUOTE_ID = "theme-preview";

const PREVIEW_BANNER_IMAGES = [
  "https://images.unsplash.com/photo-1514282401047-d79a71a590e8?auto=format&fit=crop&w=1600&q=80",
  "https://images.unsplash.com/photo-1573843981267-be1999ff37cd?auto=format&fit=crop&w=1600&q=80",
];

export function buildQuoteThemePreviewFixture(
  brandName: string,
  brand?: iBrand | null
): {
  quote: iHolidayQuoteX;
  customer: iContact;
  agent: iUser;
  organization: iOrganization;
} {
  const customer = {
    id: "preview-contact",
    data: {
      firstName: "Alex",
      lastName: "Morgan",
    },
  } as iContact;

  const agent = {
    id: "preview-agent",
    data: {
      firstName: "Sarah",
      lastName: "Johnson",
      description: "Senior Travel Consultant",
      avatar:
        "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&w=200&h=200&q=80",
      phoneNumber: "+44 20 7946 0958",
      bio: "Specialist in luxury honeymoons and Indian Ocean escapes.",
      availability_days: "Mon – Fri",
      availability_hours: "9am – 6pm",
    },
    credentials: {
      email: "sarah@example.com",
    },
  } as iUser;

  const organization = {
    id: "preview-org",
    domain: "preview.holidayquotes.app",
    data: {
      name: brandName,
      logo: "",
      primaryColor: "#F15933",
    },
  } as iOrganization;

  const agentName = "Sarah Johnson";
  const customTemplateBlock = buildPreviewCustomTemplateBlock(brand);
  const trustPilotBlock = buildPreviewTrustPilotBlock(brand);

  const pricingBlocks = [
    buildPreviewWhatsIncludedBlock(),
    buildPreviewAgentDataBlock(agentName),
  ];

  const postPricingBlocks = [
    {
      id: "preview-pricing",
      type: "pricing_option" as const,
      title: "Holiday Price",
      body: "",
      gallery: [],
      pricingOptionData: {
        name: "Premium Honeymoon Package",
        description:
          "Return Business Class flights, speedboat transfers, and all-inclusive overwater villa stay",
        totalPrice: 8499,
        currency: "GBP" as const,
        depositRequired: 1500,
        breakdown: [
          { id: "1", description: "Return flights (Business Class)", amount: 3200 },
          { id: "2", description: "Accommodation — 10 nights", amount: 4899 },
          { id: "3", description: "Transfers & honeymoon extras", amount: 400 },
        ],
      },
    },
    {
      id: "preview-cta",
      type: "cta_block" as const,
      title: "Ready to book?",
      body: "",
      gallery: [],
      ctaBlockData: {
        includeBookingConfirmation: true,
      },
    },
  ];

  const quote: iHolidayQuoteX = {
    id: QUOTE_THEME_PREVIEW_QUOTE_ID,
    shortId: 42,
    organizationId: brand?.organizationId ?? "preview-org",
    contactId: "preview-contact",
    opportunityId: "preview-opportunity",
    brandId: brand?.id ?? "preview-brand",
    data: {
      title: "Maldives Honeymoon Escape",
      departureDate: "2026-09-12",
      nights: 10,
      greetingText: buildPreviewGreetingText(
        customer.data.firstName ?? "Guest",
        agentName
      ),
      passengerCount: {
        adults: 2,
        youth: 0,
        children: 0,
        infants: 0,
      },
      bannerImages: PREVIEW_BANNER_IMAGES,
    },
    feedbacks: [],
    viewLogs: [],
    preItineraryBlocks: [],
    itineraryBlocks: buildPreviewItineraryBlocks(),
    postItineraryBlocks: [
      ...(customTemplateBlock ? [customTemplateBlock] : []),
      ...(trustPilotBlock ? [trustPilotBlock] : []),
    ],
    pricingBlocks,
    postPricingBlocks,
    metadata: {
      createdAt: Date.now(),
      createdBy: "preview-agent",
      status: "DRAFT",
    },
  };

  return { quote, customer, agent, organization };
}

export function buildQuoteThemePreviewOrganization(
  brand: {
    name?: string;
    logo?: string;
    primaryColor?: string;
    organizationId?: string;
  } | null | undefined
): iOrganization {
  return {
    id: brand?.organizationId ?? "preview-org",
    domain: "preview.holidayquotes.app",
    data: {
      name: brand?.name ?? "Your Brand",
      logo: brand?.logo ?? "",
      primaryColor: brand?.primaryColor ?? "#F15933",
    },
  } as iOrganization;
}
